import { utilityPlanRateIntervalsCollection } from '@/reducers/utilityPlanRateIntervalsCollection'
import { useMemo, useCallback } from 'react'
import { match } from 'ts-pattern'
import useCostCategories from '@/hooks/useCostCategories'
import { format } from 'date-fns'
import { TooltipItem } from 'chart.js'
import { BarGraphDataset } from '@/components/BarGraph/V2/BarGraph'

export default function useCostCategoryBarGraphViewModel(
  intervals: ReturnType<
    typeof utilityPlanRateIntervalsCollection.useFetch
  >['data'],
  costCategories: ReturnType<typeof useCostCategories>['costCategories'],
) {
  const costCategoriesBarGraphData = costCategories.map((c) => ({
    minValue: c.minValue,
    maxValue: c.maxValue,
    type: c.type,
  }))

  const costCategoriesBarGraphItems = useMemo(
    () =>
      intervals
        .filter((i) => i.cost_cents !== null)
        .map((c) => ({
          value: c.cost_cents,
          label: `${format(new Date(c.start_datetime), 'ha').toLowerCase()} • ${
            c.cost_cents
          }¢/kWh`,
          type:
            costCategoriesBarGraphData.find((cat) => {
              const costCents = c.cost_cents ?? Number.NEGATIVE_INFINITY
              return match(cat.type)
                .with(
                  'medium',
                  () => costCents >= cat.minValue && costCents <= cat.maxValue,
                )
                .otherwise(
                  () => costCents >= cat.minValue && costCents < cat.maxValue,
                )
            })?.type ?? '',
        })),
    [intervals, costCategoriesBarGraphData],
  )

  const yAxisValues = [0, 4, 8]
  const datasets = useMemo(
    () =>
      [
        {
          data: costCategoriesBarGraphItems.map((i) =>
            i.type === 'negative' ? yAxisValues[0] - 2 : null,
          ),
          backgroundColor: '#00922B',
        },
        {
          data: costCategoriesBarGraphItems.map((i) =>
            i.type === 'low' ? yAxisValues[0] + 2 : null,
          ),
          backgroundColor: '#00AB32',
        },
        {
          data: costCategoriesBarGraphItems.map((i) =>
            i.type === 'medium' ? yAxisValues[1] + 0.5 : null,
          ),
          backgroundColor: '#FBB142',
        },
        {
          data: costCategoriesBarGraphItems.map((i) =>
            i.type === 'high' ? yAxisValues[2] + 0.5 : null,
          ),
          backgroundColor: '#FB5842',
        },
      ] satisfies BarGraphDataset[],
    [costCategoriesBarGraphItems],
  )

  const labels = useMemo(
    () =>
      intervals
        .filter((i) => i.cost_cents !== null)
        .map((t, i, all) =>
          match(i)
            .with(0, () =>
              format(new Date(t.start_datetime), 'ha').toLowerCase(),
            )
            .with(Math.round(all.length / 2) - 1, () =>
              format(new Date(t.start_datetime), 'ha').toLowerCase(),
            )
            .with(all.length - 1, () =>
              format(new Date(t.start_datetime), 'ha').toLowerCase(),
            )
            .otherwise(() => ''),
        ),
    [intervals],
  )

  const toolTipCallback = useCallback(
    (context: TooltipItem<'bar'>) => {
      return costCategoriesBarGraphItems[context.dataIndex]?.label || ''
    },
    [costCategoriesBarGraphItems],
  )

  const xAxisTicksCallback = useCallback((tickValue: string | number) => {
    const value = +tickValue
    if (value === yAxisValues[0]) return 'Low'
    if (value === yAxisValues[1]) return 'Med'
    if (value === yAxisValues[2]) return 'High'
    return ''
  }, [])

  return {
    datasets,
    labels,
    yAxisValues,
    maxYAxisValue: yAxisValues[2] + 2.5,
    toolTipCallback,
    xAxisTicksCallback,
  }
}
